import { Alert } from "@mantine/core";
import type { FieldPath, UseFormReturn } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { useSafeMissingData } from "../../../../../hooks/useMissingData";
import { AlertPresets } from "../../../../../mantine/mantinePresets";
import { FoerderRegime } from "../../../../../types/api.types";
import { roundedNumericFormatter } from "../../../../../utils/RoundedNumberFormat";
import { camelize } from "../../../../../utils/SnakeCamelConversion";
import type { FormSectionProps } from "../../../../BuildingBlocks/Forms/FormSection/FormSection";
import { FormSection } from "../../../../BuildingBlocks/Forms/FormSection/FormSection";
import { CustomFormFieldWrapper } from "../../../../BuildingBlocks/Forms/MantineFormFields/CustomFormFieldWrapper/CustomFormFieldWrapper";
import { FormFieldInfoText } from "../../../../BuildingBlocks/Forms/MantineFormFields/FormFieldInfoText/FormFieldInfoText";
import {
  CompensationRateTable,
  type CompensationRateRow,
  type CompensationValueField
} from "../../../../PeriodBasedComponents/CompensationRateTable/CompensationRateTable";
import {
  MarketingTypeTable,
  type MarketingTypeRow,
  type MarketingValueField
} from "../../../../PeriodBasedComponents/MarketingTypeTable/MarketingTypeTable";
import { getVeraeusserungsformLabel } from "../../../../PeriodBasedComponents/MarketingTypeTable/MarketingTypeTable.constants";
import { getRowId } from "../../../../PeriodBasedComponents/PeriodBasedTable/hooks/usePeriodBasedTable";
import type {
  ExternalErrors,
  PeriodBasedRow
} from "../../../../PeriodBasedComponents/PeriodBasedTable/PeriodBasedTable.types";
import {
  GENERATOR_FORM_DATA,
  isVeraeusserungsformenReady
} from "../../GeneratorModal.constants";
import type { GeneratorSingleEndpointResponse } from "../../GeneratorModal.types";
import { COMPENSATION_AND_MARKETING_FIELD_NAMES } from "../EEGAnlageTab";

const EMPTY_COMPENSATION_RATE_VALUES: CompensationValueField = {
  price_ct_per_kwh: null
};
const EMPTY_MARKETING_TYPE_VALUES: MarketingValueField = {
  veraeusserungsform: ""
};

type FieldErrorArray =
  Array<Record<string, { message?: string }>> | undefined | null;

function parseArrayFieldErrors<
  T extends { first_date: string; last_date: string | null }
>(arrayErrors: FieldErrorArray, periodBasedData: T[]): ExternalErrors {
  if (!arrayErrors || !Array.isArray(arrayErrors)) {
    return {};
  }
  const reducedErrors = arrayErrors.reduce((errorsByRowId, rowError, index) => {
    if (rowError && typeof rowError === "object" && periodBasedData[index]) {
      const rowId = getRowId(periodBasedData[index]);
      errorsByRowId[rowId] = {
        first_date: rowError.first_date?.message,
        last_date: rowError.last_date?.message,
        price_ct_per_kwh: rowError.price_ct_per_kwh?.message,
        veraeusserungsform: rowError.veraeusserungsform?.message
      };
    }
    return errorsByRowId;
  }, {} as ExternalErrors);
  return reducedErrors;
}

function getInitializedPeriodData<T extends Record<string, unknown>>(
  value: unknown,
  emptyRowValues: T,
  commissioningDate: string | null | undefined,
  fallbackData?: PeriodBasedRow<T>[]
): PeriodBasedRow<T>[] {
  const data = (value as PeriodBasedRow<T>[]) || fallbackData || [];

  if (data.length === 0 && commissioningDate) {
    return [
      {
        first_date: commissioningDate,
        last_date: null,
        ...emptyRowValues
      }
    ];
  }

  if (data.length > 0 && commissioningDate && !data[0].first_date) {
    return [{ ...data[0], first_date: commissioningDate }, ...data.slice(1)];
  }

  return data;
}

interface CompensationAndMarketingSectionProps {
  generatorForm: UseFormReturn<GeneratorSingleEndpointResponse>;
  eegCommissioningDate?: string | null;
  foerderregime?: FoerderRegime | null;
  generatorId?: number;
  isFoerderungAusgelaufen: boolean;
  variantId?: number;
}

export function CompensationAndMarketingSection({
  generatorForm,
  eegCommissioningDate,
  foerderregime,
  generatorId,
  isFoerderungAusgelaufen,
  variantId
}: CompensationAndMarketingSectionProps) {
  const {
    control,
    watch,
    formState: { errors }
  } = generatorForm;
  const currentAzw = watch("einheit.eeg_anlage.anzulegender_wert")?.at(
    -1
  )?.price_ct_per_kwh;

  const currentVeraeusserungsform = watch(
    "einheit.eeg_anlage.veraeusserungsformen"
  )?.at(-1)?.veraeusserungsform;

  const anzulegenderWert = useWatch({
    control,
    name: "einheit.eeg_anlage.anzulegender_wert" as FieldPath<GeneratorSingleEndpointResponse>
  }) as CompensationRateRow[] | undefined;

  const veraeusserungsformen = useWatch({
    control,
    name: "einheit.eeg_anlage.veraeusserungsformen" as FieldPath<GeneratorSingleEndpointResponse>
  }) as MarketingTypeRow[] | undefined;
  const isEinspeiseverguetung =
    foerderregime === FoerderRegime.eeg_fixe_einspeiseverguetung;

  const isMarktpraemie =
    foerderregime === FoerderRegime.eeg_ausschreibung ||
    foerderregime === FoerderRegime.eeg_ohne_ausschreibung ||
    foerderregime ===
      FoerderRegime.eeg_innovationsausschreibung_fixe_marktpraemie ||
    foerderregime ===
      FoerderRegime.eeg_innovationsausschreibung_gleitende_marktpraemie;

  const infoContent = isEinspeiseverguetung
    ? "Bitte geben Sie bei Erhalt einer fixen Einspeisevergütung alle für Ihre Anlage relevanten Vergütungsbestanteile an. Erhalten Sie für einen Zeitraum bspw. eine Managementprämie aufgrund einer freiwilligen Direktvermarktung, addieren Sie diesen Wert dazu. Sofern Sie weitere Prämien oder Zuschläge erhalten, tragen Sie diese bitte als Gesamtsumme ein."
    : isMarktpraemie
      ? "Bitte geben Sie neben dem Anzulegenden Wert (AZW) auch weitere Zuschläge an und tragen Sie diese bitte als Gesamtsumme ein. Ihre Anlage hat während des Förderzeitraums immer einen eindeutigen Fördersatz, der sich durch gesetzliche Regelungen zeitlich verändern kann."
      : undefined;

  const azwPreview = isMarktpraemie
    ? `AZW (+ Zuschläge): ${roundedNumericFormatter(currentAzw)} ct/kWh`
    : `Einspeisevergütung (+ Zuschläge): ${roundedNumericFormatter(currentAzw)} ct/kWh`;

  const veraeusserungsformPreview = currentVeraeusserungsform
    ? getVeraeusserungsformLabel(currentVeraeusserungsform)
    : "";

  const preview = [azwPreview, veraeusserungsformPreview]
    .filter(Boolean)
    .join(" | ");

  const anzulegenderWertErrors = errors?.einheit?.eeg_anlage?.anzulegender_wert;
  const veraeusserungsformenErrors = errors?.einheit?.eeg_anlage
    ?.veraeusserungsformen as unknown as FieldErrorArray;
  const veraeusserungsformAlertErrors = isVeraeusserungsformenReady
    ? (Object.values(
        parseArrayFieldErrors(
          veraeusserungsformenErrors,
          veraeusserungsformen ?? []
        )
      )
        .map((rowErrors) => rowErrors?.veraeusserungsform)
        .filter(Boolean) as string[])
    : [];

  const hasAnzulegenderWertErrors =
    anzulegenderWertErrors &&
    !Array.isArray(anzulegenderWertErrors) &&
    !!anzulegenderWertErrors.message;
  const hasVeraeusserungsformErrors = veraeusserungsformAlertErrors.length > 0;

  const { missingData } = useSafeMissingData();
  const missingFields = Object.values(missingData?.missingFields ?? {}).flat();
  const isFieldMissing = (fieldName: string) =>
    !isFoerderungAusgelaufen &&
    missingFields.some((field) => camelize(fieldName).includes(field));
  const highlightMissingAzw = isFieldMissing(
    GENERATOR_FORM_DATA["einheit.eeg_anlage.anzulegender_wert"].name
  );
  const highlightMissingVeraeusserungsform = isFieldMissing(
    GENERATOR_FORM_DATA["einheit.eeg_anlage.veraeusserungsformen"].name
  );

  function getForcedSectionOpenState(): FormSectionProps["forcedOpenState"] {
    if (hasAnzulegenderWertErrors || hasVeraeusserungsformErrors) {
      return "open";
    }
    if (isFoerderungAusgelaufen) {
      return "closed";
    }
    return undefined;
  }

  return (
    <FormSection
      fieldNamesForMissingFields={
        isFoerderungAusgelaufen ? [] : COMPENSATION_AND_MARKETING_FIELD_NAMES
      }
      forcedOpenState={getForcedSectionOpenState()}
      hasError={hasAnzulegenderWertErrors || hasVeraeusserungsformErrors}
      isCollapsible
      label="Vergütung"
      mb="md"
      preview={currentAzw ? preview : undefined}
    >
      {!isFoerderungAusgelaufen && infoContent && (
        <Alert
          color={AlertPresets.info.color}
          icon={AlertPresets.info.icon}
          mb="md"
        >
          {infoContent}
        </Alert>
      )}
      {
        <Controller
          control={control}
          defaultValue={
            eegCommissioningDate
              ? [
                  {
                    first_date: eegCommissioningDate,
                    last_date: null,
                    price_ct_per_kwh: ""
                  }
                ]
              : []
          }
          name={
            GENERATOR_FORM_DATA["einheit.eeg_anlage.anzulegender_wert"].name
          }
          render={({ field: { onChange, value } }) => {
            const periodBasedData =
              getInitializedPeriodData<CompensationValueField>(
                value,
                EMPTY_COMPENSATION_RATE_VALUES,
                eegCommissioningDate,
                anzulegenderWert
              );

            const rootError =
              !Array.isArray(anzulegenderWertErrors) &&
              anzulegenderWertErrors?.message
                ? anzulegenderWertErrors.message
                : undefined;
            const externalErrors = parseArrayFieldErrors(
              anzulegenderWertErrors as FieldErrorArray,
              periodBasedData
            );

            return (
              <CustomFormFieldWrapper
                error={rootError}
                mb={isFoerderungAusgelaufen ? "xl" : 0}
                name={
                  GENERATOR_FORM_DATA["einheit.eeg_anlage.anzulegender_wert"]
                    .name
                }
              >
                <CompensationRateTable
                  commissioningDate={eegCommissioningDate || undefined}
                  externalErrors={externalErrors}
                  generatorId={generatorId}
                  highlightMissing={highlightMissingAzw}
                  isMarktpraemie={isMarktpraemie}
                  key={foerderregime}
                  periodBasedData={periodBasedData}
                  readOnly={isFoerderungAusgelaufen}
                  variantId={variantId}
                  onChange={onChange}
                />
              </CustomFormFieldWrapper>
            );
          }}
        />
      }

      {!isFoerderungAusgelaufen && (
        <FormFieldInfoText infoText="Ihre Anlage hat während des Förderzeitraums immer eine eindeutige Vergütung als Förderung. Über die Angabe der Veräußerungsform wird hinterlegt, ob die Förderung im gewählten Zeitraum zum Einsatz kommt." />
      )}

      {isVeraeusserungsformenReady &&
        veraeusserungsformAlertErrors.length > 0 && (
          <Alert
            color={AlertPresets.error.color}
            icon={AlertPresets.error.icon}
            mb="md"
            style={{ whiteSpace: "break-spaces" }}
          >
            {veraeusserungsformAlertErrors.join("\n")}
          </Alert>
        )}

      {isVeraeusserungsformenReady && (
        <Controller
          control={control}
          defaultValue={[]}
          name={
            GENERATOR_FORM_DATA["einheit.eeg_anlage.veraeusserungsformen"].name
          }
          render={({ field: { onChange, value } }) => {
            const rows = getInitializedPeriodData<MarketingValueField>(
              value,
              EMPTY_MARKETING_TYPE_VALUES,
              eegCommissioningDate,
              veraeusserungsformen
            );
            const externalErrors = parseArrayFieldErrors(
              veraeusserungsformenErrors as FieldErrorArray,
              rows
            );

            const tableExternalErrors = Object.fromEntries(
              Object.entries(externalErrors).map(([rowId, rowErrors]) => {
                const { veraeusserungsform: _, ...rest } = rowErrors ?? {};
                return [rowId, rest];
              })
            );

            return (
              <MarketingTypeTable
                externalErrors={tableExternalErrors}
                generatorId={generatorId}
                highlightMissing={highlightMissingVeraeusserungsform}
                isMarktpraemie={isMarktpraemie}
                key={foerderregime}
                periodBasedData={rows}
                readOnly={isFoerderungAusgelaufen}
                variantId={variantId}
                onChange={onChange}
              />
            );
          }}
        />
      )}
    </FormSection>
  );
}
